import { useState } from 'react';
import { Plus, Trash2, Image as ImageIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ImageGallery } from '@/components/glitch-ui/image-gallery';
import { useToast } from '@/hooks/useToast';
import { useRefetchCampaign } from '@/hooks/useRefetchCampaign';
import {
  useAttachImageExtensionMutationOptions,
  useDetachImageExtensionMutationOptions,
} from '@/lib/query-options';
import type { CampaignData, CreativeAsset } from '@/lib/types';

interface ImageSectionProps {
  campaign: CampaignData;
  disabled?: boolean;
}

/**
 * Image extensions (#4376): attach images from the cross-platform asset library to a campaign as
 * Google Ads ``AD_IMAGE`` assets. Attached images render as a thumbnail grid; the picker reuses the
 * existing ``ImageGallery`` in ``mode='picker'`` and toggles attach/detach per tile.
 */
export function ImageSection({ campaign, disabled = false }: ImageSectionProps) {
  const { id: campaignId, image_extensions = [] } = campaign;
  const refetchCampaign = useRefetchCampaign(campaign.slug);
  const { toast } = useToast();
  const [isPickerOpen, setIsPickerOpen] = useState(false);

  const attachMutation = useAttachImageExtensionMutationOptions();
  const detachMutation = useDetachImageExtensionMutationOptions();

  const attachedIds = image_extensions.map((asset) => asset.id);

  const handleToggle = async (asset: CreativeAsset) => {
    const isAttached = attachedIds.includes(asset.id);
    try {
      if (isAttached) {
        await detachMutation.mutateAsync({ campaignId, creativeAssetId: asset.id });
      } else {
        await attachMutation.mutateAsync({ campaignId, creativeAssetId: asset.id });
      }
      refetchCampaign();
    } catch (error) {
      toast({
        description: error instanceof Error ? error.message : `Failed to ${isAttached ? 'remove' : 'add'} image`,
        variant: 'destructive',
      });
    }
  };

  const handleRemove = async (asset: CreativeAsset) => {
    try {
      await detachMutation.mutateAsync({ campaignId, creativeAssetId: asset.id });
      refetchCampaign();
    } catch (error) {
      toast({
        description: error instanceof Error ? error.message : 'Failed to remove image',
        variant: 'destructive',
      });
    }
  };

  return (
    <div>
      {/* Header */}
      <div className='flex items-center justify-between mb-4'>
        <div>
          <h2 className='text-xl font-semibold text-gray-900'>Image Extensions</h2>
          <p className='text-sm text-gray-600 mt-1'>
            Show images alongside your text ads. {image_extensions.length} added.
          </p>
        </div>
        {!disabled && (
          <Button variant='outline' className='text-gray-900' onClick={() => setIsPickerOpen(true)}>
            <Plus className='h-4 w-4 mr-2' />
            Add Images
          </Button>
        )}
      </div>

      {/* Attached images */}
      {image_extensions.length === 0 ? (
        <div className='flex flex-col items-center justify-center rounded-lg border border-dashed border-gray-200 py-10 text-center'>
          <ImageIcon className='h-8 w-8 text-gray-300 mb-2' strokeWidth={1.5} />
          <p className='text-sm font-medium text-gray-900'>No Image Extensions</p>
          <p className='text-xs text-gray-500 mt-1'>Pick images from the asset library to show alongside your ads.</p>
        </div>
      ) : (
        <div className='grid grid-cols-4 gap-3'>
          {image_extensions.map((asset) => (
            <div
              key={asset.id}
              className='group relative aspect-square overflow-hidden rounded-lg border border-gray-200 bg-gray-50'
            >
              <img
                src={asset.signed_url}
                alt={asset.original_filename ?? 'Image extension'}
                className='h-full w-full object-cover'
              />
              {!disabled && (
                <button
                  type='button'
                  onClick={() => handleRemove(asset)}
                  aria-label='Remove image extension'
                  className='absolute top-1.5 right-1.5 rounded-md bg-white/90 p-1.5 text-gray-400 opacity-0 shadow-sm transition group-hover:opacity-100 hover:text-red-600 hover:bg-red-50'
                >
                  <Trash2 className='h-4 w-4' />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Picker dialog — reuse the existing library gallery in picker mode */}
      <Dialog open={isPickerOpen} onOpenChange={setIsPickerOpen}>
        <DialogContent className='sm:max-w-[760px]'>
          <DialogHeader>
            <DialogTitle>Add Image Extensions</DialogTitle>
          </DialogHeader>
          <ImageGallery
            mode='picker'
            title='Image library'
            baseText='Select images to show alongside your ads'
            selectedAssetIds={attachedIds}
            onSelect={handleToggle}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
